import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PiTrash, PiShieldCheck, PiLock, PiEyeSlash, PiListChecks } from "react-icons/pi";
import { post, clearUserSession } from "../utils/api";
import { captureError } from "../utils/errorHandler";
import { logInvestingActivity } from "../utils/investingActivity";
import { clearInvestingLocalState } from "../utils/onboardingRoutes";
import { InvestingAccountMenu, InvestingDeleteDataModal } from "./InvestingAccountMenu";
import toast, { toastConfig } from "react-simple-toasts";
import { track } from "../utils/analytics";

toastConfig({ position: "top-right" });

const LayoutInvesting = ({ children }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Get user email from localStorage
  const userEmail = localStorage.getItem("userEmail") || "";
  const userInitial = userEmail ? userEmail.charAt(0).toUpperCase() : "U";
  const userLabel = userEmail ? userEmail.split("@")[0] : "";

  // Set page title for investing flows
  useEffect(() => {
    document.title = "Investing - Prime Meridian";
  }, []);

  const handleLogout = () => {
    logInvestingActivity("investing_logout_clicked", {}, { flushNow: true, preferBeacon: true });
    post("/auth/sign-out/", {})
      .then((response) => {
        const message = response.data?.message || "Signed out successfully";
        toast(message, { theme: "success" });
        clearUserSession();
        navigate("/login");
      })
      .catch((error) => {
        captureError(error, "logging out from investing app");
        const errorMessage =
          error.response?.data?.error || "Logout failed. Please try again.";
        toast(errorMessage, { theme: "failure" });
        // Clear local data and redirect even if the sign-out call failed.
        clearUserSession();
        navigate("/login");
      });
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    track("investing_delete_my_data_confirm_shown");
    logInvestingActivity("investing_delete_data_modal_opened", {});
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    track("investing_delete_my_data_confirmed");
    logInvestingActivity("investing_delete_data_modal_confirm_clicked", {});

    try {
      await post("/investing/delete-my-data/");
      toast("Your investing data has been deleted successfully! Redirecting...", { theme: "success" });
      track("investing_delete_my_data_success");
      // Beacon-flushed: the page navigates away 1.5s from now, and the queue's
      // normal 5s flush would lose the row.
      logInvestingActivity("investing_delete_data_succeeded", {}, { flushNow: true, preferBeacon: true });

      clearInvestingLocalState();
      setShowDeleteConfirm(false);
      setDeleteConfirmText("");

      // Wait briefly so the user sees the toast, then reload onto the first screen.
      setTimeout(() => {
        window.location.href = "/investing/upload";
      }, 1500);
    } catch (error) {
      captureError(error, "delete my data from investing app");
      track("investing_delete_my_data_error");
      const errorMsg = error.response?.data?.error || "Failed to delete data. Please try again.";
      logInvestingActivity("investing_delete_data_failed", {
        error: String(errorMsg).slice(0, 300),
      });
      toast(errorMsg, { theme: "failure" });
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmText("");
    track("investing_delete_my_data_confirm_cancelled");
    logInvestingActivity("investing_delete_data_cancelled", {});
  };

  const handleSwitchToMainApp = () => {
    logInvestingActivity("investing_switched_to_main_app", { from: "investing_onboarding_header" });
    navigate("/onboarding");
  };

  return (
    <div className="onboard-layout">
      <header className="border-b border-[#E5E5E5] shrink-0">
        {/* Desktop Header */}
        <div className="onboard-header-pc relative mx-auto py-6 px-8 lg:px-16">
          <div className="flex items-center">
            <p className="font-lora font-semibold text-lg text-black">
              Prime Meridian
            </p>
          </div>

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <h1 className="text-[20px] font-semibold text-[#717171]">
              Investing Onboarding
            </h1>
          </div>

          {/* Desktop Account Dropdown */}
          <div className="absolute right-8 lg:right-16 top-1/2 -translate-y-1/2">
            <InvestingAccountMenu
              showLabel
              userEmail={userEmail}
              userInitial={userInitial}
              userLabel={userLabel}
              onLogout={handleLogout}
              onDeleteClick={handleDeleteClick}
              onSwitchToMainApp={handleSwitchToMainApp}
            />
          </div>
        </div>

        {/* Mobile Header */}
        <div className="onboard-header-sp flex items-center justify-between pt-[20px] pb-[15px] px-4">
          <div>
            <p className="font-lora font-semibold text-base text-black">
              Prime Meridian
            </p>
            <p className="font-lora font-semibold text-sm text-[#717171]">
              Investing Onboarding
            </p>
          </div>
          {/* Mobile Account Dropdown */}
          <InvestingAccountMenu
            userEmail={userEmail}
            userInitial={userInitial}
            userLabel={userLabel}
            onLogout={handleLogout}
            onDeleteClick={handleDeleteClick}
            onSwitchToMainApp={handleSwitchToMainApp}
          />
        </div>
      </header>

      <InvestingDeleteDataModal
        show={showDeleteConfirm}
        isDeleting={isDeleting}
        confirmText={deleteConfirmText}
        setConfirmText={setDeleteConfirmText}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <main className="flex-1 px-4 md:px-8 lg:px-16 py-6 pb-8 md:py-8 md:pb-12">
        {children}

        {/* Security Trust Section */}
        <div className="w-full max-w-3xl mx-auto mt-12 md:mt-16">
          <div className="border border-[#E5E5E5] rounded-xl bg-white/60 p-5 md:p-6">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4 md:mb-5">
              <PiShieldCheck className="w-5 h-5 text-[#16A34A]" />
              <span className="text-base font-semibold text-[#1c1c1c]">Your data is protected</span>
            </div>

            {/* Grid - 1 column on mobile, 2 columns on desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {/* Bank-Level Encryption */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                  <PiLock className="w-4 h-4 text-[#717171]" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-[#1c1c1c]">Bank-Level Encryption</span>
                  <span className="text-xs text-[#717171] leading-relaxed">256-bit AES encryption protects your data in transit and at rest</span>
                </div>
              </div>

              {/* Never Sold or Trained */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                  <PiEyeSlash className="w-4 h-4 text-[#717171]" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-[#1c1c1c]">Never Sold or Trained</span>
                  <span className="text-xs text-[#717171] leading-relaxed">Your data is never sold, or used for AI model training</span>
                </div>
              </div>

              {/* IRS Security Six */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                  <PiListChecks className="w-4 h-4 text-[#717171]" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-[#1c1c1c]">IRS Security Six</span>
                  <span className="text-xs text-[#717171] leading-relaxed">All six IRS-recommended safeguards for tax data protection</span>
                </div>
              </div>

              {/* Delete Anytime */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                  <PiTrash className="w-4 h-4 text-[#717171]" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-[#1c1c1c]">Delete Anytime</span>
                  <span className="text-xs text-[#717171] leading-relaxed">Permanently remove all your data with one click</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="shrink-0 py-4 text-center">
        <p className="text-sm text-[#717171]">
          Having issues?{" "}
          <a
            href="mailto:help@getprimemeridian.com"
            className="text-black hover:text-[#8C6222] transition-colors"
          >
            help@getprimemeridian.com
          </a>
        </p>
      </footer>
    </div>
  );
};

export default LayoutInvesting;
